import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  STATUS_LABELS,
  STATUS_COLORS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
  CATEGORIES,
  PRIORITIES,
  STATUSES,
  formatDate,
  RECURRENCE_COLORS,
  OCC_CLASS_SHORT,
  OCC_CLASS_COLORS,
  OCC_CLASS_LABELS,
} from "@/lib/labels";
import SlaBadge from "@/components/SlaBadge";
import RecordActions from "@/components/RecordActions";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import ReviewDialog from "@/components/ReviewDialog";
import OccurrenceEditDialog from "@/components/OccurrenceEditDialog";
import OccurrenceAssignDialog from "@/components/OccurrenceAssignDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PlusCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  Inbox,
  X,
  FileText,
  Loader2,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ALL = "__all__";

export default function Occurrences() {
  const navigate = useNavigate();
  const { user, can } = useAuth();

  /*
   * ============================================================
   * FILTROS PERSISTENTES
   * ============================================================
   *
   * Os filtros ficam na URL.
   *
   * Exemplo:
   * /ocorrencias?client_id=xxx&unit_id=yyy
   *
   * Isso faz com que o filtro sobreviva à navegação para o detalhe
   * e ao retorno para a listagem.
   */
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState({
    items: [],
    total: 0,
    pages: 1,
  });

  const [page, setPage] = useState(
    Number(searchParams.get("page") || 1)
  );

  const [search, setSearch] = useState(
    searchParams.get("search") || ""
  );

  const [status, setStatus] = useState(
    searchParams.get("status") || ALL
  );

  const [priority, setPriority] = useState(
    searchParams.get("priority") || ALL
  );

  const [category, setCategory] = useState(
    searchParams.get("category") || ALL
  );

  const [occClass, setOccClass] = useState(
    searchParams.get("occurrence_class") || ALL
  );

  const [clientId, setClientId] = useState(
    searchParams.get("client_id") || ALL
  );

  const [unitId, setUnitId] = useState(
    searchParams.get("unit_id") || ALL
  );

  const [clients, setClients] = useState([]);
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES);

  const [loading, setLoading] = useState(true);

  const [editTarget, setEditTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfDate, setPdfDate] = useState(() => {
    const now = new Date();
    const local = new Date(
      now.getTime() -
        now.getTimezoneOffset() * 60000
    );
    return local.toISOString().slice(0, 10);
  });
  const [pdfClientId, setPdfClientId] =
    useState(ALL);
  const [pdfUnitId, setPdfUnitId] =
    useState(ALL);
  const [pdfCategory, setPdfCategory] =
    useState(ALL);
  const [pdfOccurrenceClass, setPdfOccurrenceClass] =
    useState(ALL);

  /*
   * ============================================================
   * CARREGAMENTO DE CLIENTES / UNIDADES / CATEGORIAS
   * ============================================================
   */
  useEffect(() => {
    api
      .get("/taxonomy/full")
      .then(({ data }) => {
        setCategories(
          data.map((c) => c.name)
        );
      })
      .catch(() => {});

    if (user.role !== "cliente") {
      api
        .get("/clients")
        .then(({ data }) => setClients(data))
        .catch(() => {});

      api
        .get("/units")
        .then(({ data }) => setUnits(data))
        .catch(() => {});
    }
  }, [user.role]);


  /*
   * ============================================================
   * SINCRONIZA ESTADO DOS FILTROS COM A URL
   * ============================================================
   */
  useEffect(() => {
    const next = new URLSearchParams();

    if (clientId !== ALL) {
      next.set("client_id", clientId);
    }

    if (unitId !== ALL) {
      next.set("unit_id", unitId);
    }

    if (search) {
      next.set("search", search);
    }

    if (status !== ALL) {
      next.set("status", status);
    }

    if (priority !== ALL) {
      next.set("priority", priority);
    }

    if (category !== ALL) {
      next.set("category", category);
    }

    if (occClass !== ALL) {
      next.set("occurrence_class", occClass);
    }

    if (page > 1) {
      next.set("page", String(page));
    }

    /*
     * replace evita criar uma entrada no histórico para cada
     * pequena alteração de filtro.
     *
     * O histórico importante continua sendo:
     * lista filtrada -> detalhe -> voltar.
     */
    setSearchParams(next, {
      replace: true,
    });
  }, [
    clientId,
    unitId,
    search,
    status,
    priority,
    category,
    occClass,
    page,
    setSearchParams,
  ]);


  /*
   * ============================================================
   * CARREGAMENTO DAS OCORRÊNCIAS
   * ============================================================
   */
  const load = useCallback(() => {
    setLoading(true);

    const params = {
      page,
      limit: 10,
    };

    if (clientId !== ALL) {
      params.client_id = clientId;
    }

    if (unitId !== ALL) {
      params.unit_id = unitId;
    }

    if (search) {
      params.search = search;
    }

    if (status !== ALL) {
      params.status = status;
    }

    if (priority !== ALL) {
      params.priority = priority;
    }

    if (category !== ALL) {
      params.category = category;
    }

    if (occClass !== ALL) {
      params.occurrence_class = occClass;
    }

    api
      .get("/occurrences", { params })
      .then(({ data }) => {
        setData(data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    page,
    search,
    status,
    priority,
    category,
    occClass,
    clientId,
    unitId,
  ]);


  useEffect(() => {
    load();
  }, [load]);


  /*
   * ============================================================
   * ALTERAÇÃO DE FILTROS
   * ============================================================
   */

  const handleClientChange = (value) => {
    setClientId(value);

    /*
     * Ao trocar o cliente, a unidade anterior deixa de ser válida.
     */
    setUnitId(ALL);

    /*
     * Sempre começa novamente da primeira página.
     */
    setPage(1);
  };


  const handleUnitChange = (value) => {
    setUnitId(value);
    setPage(1);
  };


  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };


  const handleStatusChange = (value) => {
    setStatus(value);
    setPage(1);
  };


  const handlePriorityChange = (value) => {
    setPriority(value);
    setPage(1);
  };


  const handleCategoryChange = (value) => {
    setCategory(value);
    setPage(1);
  };


  const handleOccClassChange = (value) => {
    setOccClass(value);
    setPage(1);
  };


  /*
   * ============================================================
   * LIMPAR FILTROS
   * ============================================================
   *
   * É SOMENTE aqui que todos os filtros são removidos.
   */
  const clearFilters = () => {
    setSearch("");
    setStatus(ALL);
    setPriority(ALL);
    setCategory(ALL);
    setOccClass(ALL);
    setClientId(ALL);
    setUnitId(ALL);
    setPage(1);

    setSearchParams({}, {
      replace: true,
    });
  };


  const hasFilters =
    search ||
    status !== ALL ||
    priority !== ALL ||
    category !== ALL ||
    occClass !== ALL ||
    clientId !== ALL ||
    unitId !== ALL;



  /*
   * ============================================================
   * RELATÓRIO PDF DIÁRIO
   * ============================================================
   */
  const generatePdf = async () => {
    if (!pdfDate) return;

    setPdfLoading(true);

    try {
      const params = new URLSearchParams();
      params.set("report_date", pdfDate);

      if (pdfClientId !== ALL) {
        params.set("client_id", pdfClientId);
      }

      if (pdfUnitId !== ALL) {
        params.set("unit_id", pdfUnitId);
      }

      if (pdfCategory !== ALL) {
        params.set("category", pdfCategory);
      }

      if (pdfOccurrenceClass !== ALL) {
        params.set(
          "occurrence_class",
          pdfOccurrenceClass
        );
      }

      const response = await api.get(
        `/occurrences/report/pdf?${params.toString()}`,
        { responseType: "blob" }
      );

      const blob = new Blob(
        [response.data],
        { type: "application/pdf" }
      );

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = url;
      anchor.download =
        `ocorrencias-${pdfDate}.pdf`;

      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      window.URL.revokeObjectURL(url);
      setPdfOpen(false);
    } catch (error) {
      console.error(
        "Falha ao gerar relatório PDF de ocorrências:",
        error
      );
    } finally {
      setPdfLoading(false);
    }
  };

  /*
   * ============================================================
   * EDIÇÃO
   * ============================================================
   */
  const openEdit = async (o) => {
    const { data } = await api.get(
      `/occurrences/${o.id}`
    );

    setEditTarget(data);
  };


  return (
    <div
      className="space-y-6"
      data-testid="occurrences-page"
    >

      {/* ====================================================== */}
      {/* CABEÇALHO */}
      {/* ====================================================== */}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">

        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            Ocorrências
          </h1>

          <p className="text-sm text-muted-foreground mt-1">
            {data.total} registros encontrados
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPdfOpen(true)}
            data-testid="generate-occurrences-pdf-btn"
          >
            <FileText
              size={16}
              className="mr-2"
            />
            Gerar PDF
          </Button>

          {can("ocorrencias", "create") && (
            <Button
              onClick={() =>
                navigate("/ocorrencias/nova")
              }
              data-testid="new-occurrence-btn"
            >
              <PlusCircle
                size={16}
                className="mr-2"
              />
              Nova Ocorrência
            </Button>
          )}
        </div>

      </div>


      {/* ====================================================== */}
      {/* FILTROS */}
      {/* ====================================================== */}

      <div className="flex flex-col gap-3">

        <div className="flex flex-col lg:flex-row gap-3">

          {/* BUSCA */}
          <div className="relative flex-1">

            <Search
              size={15}
              className="absolute left-3 top-1/4 -translate-y-1/2 text-muted-foreground"
            />

            <Input
              value={search}
              onChange={(e) =>
                handleSearchChange(
                  e.target.value
                )
              }
              placeholder="Buscar por protocolo ou descrição..."
              className="pl-9"
              data-testid="occurrences-search-input"
            />

          </div>


          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:w-auto">

            {/* CLIENTE */}
            <Select
              value={clientId}
              onValueChange={handleClientChange}
            >
              <SelectTrigger
                className="w-full lg:w-44"
                data-testid="filter-client"
              >
                <SelectValue placeholder="Cliente" />
              </SelectTrigger>

              <SelectContent>

                <SelectItem value={ALL}>
                  Todos os clientes
                </SelectItem>

                {clients.map((client) => (
                  <SelectItem
                    key={client.id}
                    value={client.id}
                  >
                    {client.name}
                  </SelectItem>
                ))}

              </SelectContent>
            </Select>


            {/* UNIDADE */}
            <Select
              value={unitId}
              onValueChange={handleUnitChange}
              disabled={clientId === ALL}
            >
              <SelectTrigger
                className="w-full lg:w-44"
                data-testid="filter-unit"
              >
                <SelectValue placeholder="Unidade" />
              </SelectTrigger>

              <SelectContent>

                <SelectItem value={ALL}>
                  Todas as unidades
                </SelectItem>

                {units
                  .filter(
                    (unit) =>
                      clientId === ALL ||
                      unit.client_id === clientId
                  )
                  .map((unit) => (
                    <SelectItem
                      key={unit.id}
                      value={unit.id}
                    >
                      {unit.name}
                    </SelectItem>
                  ))}

              </SelectContent>
            </Select>


            {/* STATUS */}
            <Select
              value={status}
              onValueChange={handleStatusChange}
            >
              <SelectTrigger
                className="w-full lg:w-40"
                data-testid="filter-status"
              >
                <SelectValue placeholder="Status" />
              </SelectTrigger>

              <SelectContent>

                <SelectItem value={ALL}>
                  Todos os status
                </SelectItem>

                {STATUSES.map((s) => (
                  <SelectItem
                    key={s}
                    value={s}
                  >
                    {STATUS_LABELS[s]}
                  </SelectItem>
                ))}

              </SelectContent>
            </Select>


            {/* PRIORIDADE */}
            <Select
              value={priority}
              onValueChange={handlePriorityChange}
            >
              <SelectTrigger
                className="w-full lg:w-36"
                data-testid="filter-priority"
              >
                <SelectValue placeholder="Prioridade" />
              </SelectTrigger>

              <SelectContent>

                <SelectItem value={ALL}>
                  Todas
                </SelectItem>

                {PRIORITIES.map((p) => (
                  <SelectItem
                    key={p}
                    value={p}
                  >
                    {PRIORITY_LABELS[p]}
                  </SelectItem>
                ))}

              </SelectContent>
            </Select>


            {/* CATEGORIA */}
            <Select
              value={category}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger
                className="w-full lg:w-44"
                data-testid="filter-category"
              >
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>

              <SelectContent>

                <SelectItem value={ALL}>
                  Todas categorias
                </SelectItem>

                {categories.map((c) => (
                  <SelectItem
                    key={c}
                    value={c}
                  >
                    {c}
                  </SelectItem>
                ))}

              </SelectContent>
            </Select>


            {/* CLASSIFICAÇÃO */}
            {user.role !== "cliente" && (
              <Select
                value={occClass}
                onValueChange={handleOccClassChange}
              >
                <SelectTrigger
                  className="w-full lg:w-40"
                  data-testid="filter-class"
                >
                  <SelectValue placeholder="Classificação" />
                </SelectTrigger>

                <SelectContent>

                  <SelectItem value={ALL}>
                    Cliente + Astral
                  </SelectItem>

                  {Object.entries(
                    OCC_CLASS_LABELS
                  ).map(([k, v]) => (
                    <SelectItem
                      key={k}
                      value={k}
                    >
                      {v}
                    </SelectItem>
                  ))}

                </SelectContent>
              </Select>
            )}

          </div>

        </div>


        {/* ==================================================== */}
        {/* LIMPAR FILTROS */}
        {/* ==================================================== */}

        {hasFilters && (
          <div className="flex items-center justify-between">

            <div className="text-xs text-muted-foreground">
              Filtros ativos
              {clientId !== ALL && " • Cliente"}
              {unitId !== ALL && " • Unidade"}
              {status !== ALL && " • Status"}
              {priority !== ALL && " • Prioridade"}
              {category !== ALL && " • Categoria"}
              {occClass !== ALL && " • Classificação"}
              {search && " • Busca"}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              data-testid="clear-occurrence-filters"
            >
              <X
                size={14}
                className="mr-2"
              />

              Limpar filtros
            </Button>

          </div>
        )}

      </div>


      {/* ====================================================== */}
      {/* TABELA */}
      {/* ====================================================== */}

      <div className="rounded-lg border border-border bg-card overflow-hidden">

        <div className="overflow-x-auto scrollbar-thin">

          <table className="w-full text-sm">

            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border bg-muted/40">

                <th className="px-4 py-3 font-medium">
                  Protocolo
                </th>

                <th className="px-4 py-3 font-medium">
                  Cliente / Unidade
                </th>

                <th className="px-4 py-3 font-medium hidden md:table-cell">
                  Área
                </th>

                <th className="px-4 py-3 font-medium hidden sm:table-cell">
                  Categoria
                </th>

                <th className="px-4 py-3 font-medium">
                  Prioridade
                </th>

                <th className="px-4 py-3 font-medium">
                  Status
                </th>

                <th className="px-4 py-3 font-medium">
                  SLA
                </th>

                <th className="px-4 py-3 font-medium hidden xl:table-cell">
                  Responsável
                </th>

                <th className="px-4 py-3 font-medium hidden lg:table-cell">
                  Data
                </th>

                <th className="px-4 py-3 font-medium w-10"></th>

              </tr>
            </thead>


            <tbody>

              {loading && (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-12 text-center text-muted-foreground"
                  >
                    Carregando...
                  </td>
                </tr>
              )}


              {!loading &&
                data.items.length === 0 && (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-12 text-center text-muted-foreground"
                    >
                      <Inbox
                        size={28}
                        className="mx-auto mb-2 opacity-40"
                      />

                      Nenhuma ocorrência encontrada.
                    </td>
                  </tr>
                )}


              {!loading &&
                data.items.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() =>
                      navigate(
                        `/ocorrencias/${o.id}`
                      )
                    }
                    className="border-b border-border/50 last:border-0 cursor-pointer hover:bg-accent/60 transition-colors duration-150"
                    data-testid={`occurrence-row-${o.protocol}`}
                  >

                    <td className="px-4 py-3">

                      <p className="font-mono text-xs font-semibold text-primary">
                        {o.protocol}
                      </p>

                      <div className="flex gap-1 mt-1 flex-wrap">

                        <span
                          className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${
                            OCC_CLASS_COLORS[
                              o.occurrence_class ||
                              "cliente"
                            ]
                          }`}
                          data-testid={`class-badge-${o.protocol}`}
                        >
                          {
                            OCC_CLASS_SHORT[
                              o.occurrence_class ||
                              "cliente"
                            ]
                          }
                        </span>

                        {o.recurrence?.level &&
                          o.recurrence.level !==
                            "normal" && (
                            <span
                              className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${
                                RECURRENCE_COLORS[
                                  o.recurrence.level
                                ]
                              }`}
                              data-testid={`recurrence-badge-${o.protocol}`}
                            >
                              {o.recurrence.count}ª vez
                            </span>
                          )}

                      </div>

                    </td>


                    <td className="px-4 py-3">

                      <p className="font-medium">
                        {o.client_name}
                      </p>

                      <p className="text-xs text-muted-foreground">
                        {o.unit_name}
                      </p>

                    </td>


                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">
                      {o.area_name} · {o.subarea_name}
                    </td>


                    <td className="px-4 py-3 hidden sm:table-cell">
                      {o.category}
                    </td>


                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        className={
                          PRIORITY_COLORS[
                            o.priority
                          ]
                        }
                      >
                        {
                          PRIORITY_LABELS[
                            o.priority
                          ]
                        }
                      </Badge>
                    </td>


                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        className={
                          STATUS_COLORS[
                            o.status
                          ]
                        }
                      >
                        {
                          STATUS_LABELS[
                            o.status
                          ]
                        }
                      </Badge>
                    </td>


                    <td className="px-4 py-3">
                      <SlaBadge
                        sla={o.sla_info}
                      />
                    </td>


                    <td className="px-4 py-3 hidden xl:table-cell text-xs text-muted-foreground">
                      {o.assigned_to_name ? (
                        <span
                          data-testid={`assigned-tech-${o.protocol}`}
                          className="font-medium text-foreground"
                        >
                          {o.assigned_to_name}
                        </span>
                      ) : (
                        o.responsible_name ||
                        "—"
                      )}
                    </td>


                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">
                      {formatDate(
                        o.created_at
                      )}
                    </td>


                    <td className="px-2 py-3">

                      <RecordActions
                        module="ocorrencias"
                        testId={o.protocol}
                        onView={() =>
                          navigate(
                            `/ocorrencias/${o.id}`
                          )
                        }
                        onEdit={() =>
                          openEdit(o)
                        }
                        onAssign={() =>
                          setAssignTarget(o)
                        }
                        onReview={() =>
                          setReviewTarget(o)
                        }
                        onDelete={() =>
                          setDeleteTarget(o)
                        }
                      />

                    </td>

                  </tr>
                ))}

            </tbody>

          </table>

        </div>


        {/* ==================================================== */}
        {/* PAGINAÇÃO */}
        {/* ==================================================== */}

        <div className="flex items-center justify-between px-4 py-3 border-t border-border">

          <p className="text-xs text-muted-foreground">
            Página {data.page || page} de{" "}
            {data.pages}
          </p>

          <div className="flex gap-2">

            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() =>
                setPage(page - 1)
              }
              data-testid="pagination-prev-btn"
            >
              <ChevronLeft size={14} />
            </Button>

            <Button
              variant="outline"
              size="sm"
              disabled={
                page >= data.pages
              }
              onClick={() =>
                setPage(page + 1)
              }
              data-testid="pagination-next-btn"
            >
              <ChevronRight size={14} />
            </Button>

          </div>

        </div>

      </div>


      {/* ====================================================== */}
      {/* DIALOGS */}
      {/* ====================================================== */}


      <Dialog
        open={pdfOpen}
        onOpenChange={(open) => {
          if (!pdfLoading) setPdfOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={18} />
              Gerar relatório diário
            </DialogTitle>
            <DialogDescription>
              Selecione os filtros. O PDF buscará todas as
              ocorrências do dia dentro do escopo autorizado.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <label
                htmlFor="pdf-report-date"
                className="text-sm font-medium"
              >
                Data
              </label>
              <Input
                id="pdf-report-date"
                type="date"
                value={pdfDate}
                onChange={(event) =>
                  setPdfDate(event.target.value)
                }
                disabled={pdfLoading}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="grid gap-2">
                <label className="text-sm font-medium">
                  Cliente
                </label>
                <Select
                  value={pdfClientId}
                  onValueChange={(value) => {
                    setPdfClientId(value);
                    setPdfUnitId(ALL);
                  }}
                  disabled={pdfLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os clientes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>
                      Todos os clientes
                    </SelectItem>
                    {clients.map((client) => (
                      <SelectItem
                        key={client.id}
                        value={client.id}
                      >
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium">
                  Unidade
                </label>
                <Select
                  value={pdfUnitId}
                  onValueChange={setPdfUnitId}
                  disabled={
                    pdfLoading ||
                    pdfClientId === ALL
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as unidades" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>
                      Todas as unidades
                    </SelectItem>
                    {units
                      .filter(
                        (unit) =>
                          pdfClientId === ALL ||
                          unit.client_id === pdfClientId
                      )
                      .map((unit) => (
                        <SelectItem
                          key={unit.id}
                          value={unit.id}
                        >
                          {unit.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="grid gap-2">
                <label className="text-sm font-medium">
                  Categoria
                </label>
                <Select
                  value={pdfCategory}
                  onValueChange={setPdfCategory}
                  disabled={pdfLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as categorias" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>
                      Todas as categorias
                    </SelectItem>
                    {categories.map((item) => (
                      <SelectItem
                        key={item}
                        value={item}
                      >
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium">
                  Classificação
                </label>
                <Select
                  value={pdfOccurrenceClass}
                  onValueChange={setPdfOccurrenceClass}
                  disabled={pdfLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Cliente + Astral" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>
                      Cliente + Astral
                    </SelectItem>
                    {Object.entries(
                      OCC_CLASS_LABELS
                    ).map(([key, label]) => (
                      <SelectItem
                        key={key}
                        value={key}
                      >
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
              Ordenação:{" "}
              <strong className="text-foreground">
                Crítica → Alta → Média → Baixa
              </strong>.
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPdfOpen(false)}
              disabled={pdfLoading}
            >
              Cancelar
            </Button>

            <Button
              onClick={generatePdf}
              disabled={pdfLoading || !pdfDate}
              data-testid="confirm-generate-occurrences-pdf"
            >
              {pdfLoading ? (
                <Loader2
                  size={16}
                  className="mr-2 animate-spin"
                />
              ) : (
                <FileText
                  size={16}
                  className="mr-2"
                />
              )}
              {pdfLoading ? "Gerando..." : "Gerar PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OccurrenceEditDialog
        open={!!editTarget}
        onOpenChange={(v) =>
          !v && setEditTarget(null)
        }
        occurrence={editTarget}
        onSaved={load}
      />

      <OccurrenceAssignDialog
        open={!!assignTarget}
        onOpenChange={(v) =>
          !v && setAssignTarget(null)
        }
        occurrence={assignTarget}
        onDone={load}
      />

      <ReviewDialog
        open={!!reviewTarget}
        onOpenChange={(v) =>
          !v && setReviewTarget(null)
        }
        entity="ocorrência"
        entityLabel={reviewTarget?.protocol}
        url={`/occurrences/${reviewTarget?.id}/review`}
        onDone={load}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) =>
          !v && setDeleteTarget(null)
        }
        entity="ocorrência"
        entityLabel={deleteTarget?.protocol}
        impactUrl={
          deleteTarget
            ? `/occurrences/${deleteTarget.id}/delete-impact`
            : null
        }
        deleteUrl={`/occurrences/${deleteTarget?.id}`}
        requireReason
        onDeleted={load}
      />

    </div>
  );
}